import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { apiClient } from "../api/client"; // circular: client.ts reads token that AuthProvider sets
import { JWT_SECRET } from "../config";
import type { User, AuthState } from "../types";

const AuthContext = createContext<AuthState | null>(null);

function decodeJWT(token: string): { sub: string; name: string; email: string; role: string } | null {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem("vulntask_token");
    if (stored) {
      const decoded = decodeJWT(stored);
      if (decoded) {
        setToken(stored);
        setUser({
          id: decoded.sub,
          email: decoded.email,
          name: decoded.name,
          avatar: `https://ui-avatars.com/api/?name=${decoded.name}`,
          role: decoded.role as User["role"],
        });
      }
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data } = await apiClient.post("/api/v1/auth/login", { email, password });
    const newToken = data.data.token;
    localStorage.setItem("vulntask_token", newToken);
    const decoded = decodeJWT(newToken);
    setToken(newToken);
    if (decoded) {
      setUser({
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        avatar: `https://ui-avatars.com/api/?name=${decoded.name}`,
        role: decoded.role as User["role"],
      });
    }
    // Debug: verify secret matches
    console.debug("[Auth] JWT_SECRET configured:", JWT_SECRET.substring(0, 20) + "...");
  };

  const signOut = () => {
    localStorage.removeItem("vulntask_token");
    setUser(null);
    setToken(null);
    window.location.href = "/login";
  };

  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
